import { pluginFactory } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';

import type {
	AiQuickPromptAction,
	AiQuickPromptState,
	EligibleRange,
	EligibleResult,
} from './types';

export const pluginKey = new PluginKey<AiQuickPromptState>('aiQuickPromptPlugin');

export const initialState: AiQuickPromptState = {
	isPromptVisible: false,
	eligibleRange: undefined,
	lastEligibleResult: undefined,
};

export function reducer(
	state: AiQuickPromptState,
	action: AiQuickPromptAction,
): AiQuickPromptState {
	switch (action.type) {
		case 'SHOW_PROMPT':
			return action.range
				? {
						isPromptVisible: true,
						eligibleRange: action.range,
						lastEligibleResult: undefined,
					}
				: state;
		case 'HIDE_PROMPT':
			return {
				isPromptVisible: false,
				eligibleRange: undefined,
				lastEligibleResult: action.lastEligibleResult,
			};
		default:
			return state;
	}
}

export const { createPluginState, createCommand, getPluginState } = pluginFactory(
	pluginKey,
	reducer,
	{
		mapping: (tr, pluginState) => {
			let newState = pluginState;

			if (newState.eligibleRange) {
				const newFrom = tr.mapping.map(newState.eligibleRange.from);
				const newTo = tr.mapping.map(newState.eligibleRange.to);
				if (newFrom !== newState.eligibleRange.from || newTo !== newState.eligibleRange.to) {
					newState = {
						...newState,
						eligibleRange: { from: newFrom, to: newTo },
					};
				}
			}

			return newState;
		},

		// Hide the prompt when the selection changes
		onSelectionChanged: (_tr, pluginState, editorState) => {
			let newState = pluginState;

			if (newState.isPromptVisible && newState.eligibleRange) {
				const { from, to } = newState.eligibleRange;
				const sel = editorState.selection;
				if (sel.from !== from || sel.to !== to) {
					newState = {
						...newState,
						isPromptVisible: false,
						eligibleRange: undefined,
					};
				}
			}

			return newState;
		},
	},
);

export const createShowPromptCommand = (range: EligibleRange) =>
	createCommand<AiQuickPromptAction>(() => {
		return {
			type: 'SHOW_PROMPT',
			range,
		};
	});

export const createHidePromptCommand = (lastEligibleResult?: EligibleResult) =>
	createCommand<AiQuickPromptAction>({
		type: 'HIDE_PROMPT',
		lastEligibleResult,
	});
